<script setup lang="ts">
import type { InstallationValue, ReturnValue } from '@/utils/PortalConstants';
import type { FieldSize } from '@/components/fields/KSelectField.vue';

import { computed } from 'vue';

import {
  installationOptions,
  installationOptionsText,
  returnOptions,
  returnOptionsTexts,
} from '@/utils/PortalConstants';
import KSelectField from '@/components/fields/KSelectField.vue';
import { requiredValidationRule } from '@/helpers/validationRules';

export interface Props {
  modelValue?: InstallationValue | ReturnValue;
  isReturnLeg?: boolean;
  size?: FieldSize;
  disabled?: boolean;
  readonly?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  isReturnLeg: false,
  size: 'md',
  disabled: false,
  readonly: false,
});

const emit = defineEmits<{
  'update:modelValue': [value: InstallationValue | ReturnValue];
}>();

const items = computed(() => {
  const source = props.isReturnLeg ? returnOptions : installationOptions;
  return source.map((o) => ({ value: o.value, title: o.text }));
});

const label = computed(() => (props.isReturnLeg ? 'Return option' : 'Service'));

function isReturnValue(
  value: InstallationValue | ReturnValue,
): value is ReturnValue {
  return returnOptions.some((option) => option.value === value);
}

function isInstallationValue(
  value: InstallationValue | ReturnValue,
): value is InstallationValue {
  return installationOptions.some((option) => option.value === value);
}

const message = computed<string>(() => {
  const value = props.modelValue;
  if (!value) return '';
  if (props.isReturnLeg) {
    return isReturnValue(value) ? (returnOptionsTexts[value] ?? '') : '';
  }
  return isInstallationValue(value)
    ? (installationOptionsText[value] ?? '')
    : '';
});

// KSelectField with `auto-select-first` misinterprets empty strings — coerce
// `''` to `null` so the field renders empty instead of auto-picking an item.
const innerValue = computed(() => props.modelValue || null);

function handleUpdate(value: unknown): void {
  // KSelectField values are strings from installationOptions/returnOptions.
  // Fallback to empty string if null (empty is a valid value in the union types).
  if (value == null) {
    emit('update:modelValue', '' as InstallationValue | ReturnValue);
    return;
  }
  if (typeof value !== 'string') {
    return;
  }
  emit('update:modelValue', value as InstallationValue | ReturnValue);
}
</script>

<template>
  <KSelectField
    :model-value="innerValue"
    :items="items"
    :label="label"
    :size="props.size"
    :disabled="props.disabled"
    :readonly="props.readonly"
    :messages="message"
    :rules="[requiredValidationRule]"
    validate-on="input"
    class="jobBookingServiceOrReturnField"
    data-testid="jobBookingServiceOrReturnField"
    @update:model-value="handleUpdate"
  />
</template>
